import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ChatHistory, ChatHistoryGroup } from 'src/app/models/history.interface';
import { Lead, LeadStatus, PaginatedLeadsResponse } from 'src/app/models/lead.interface';
import { BotService } from 'src/app/core/services/bot.service';
import { DashboardService } from 'src/app/core/services/dashboard.service';
import { DateFormatService } from 'src/app/core/services/date-format.service';
import { PermissionService } from 'src/app/core/services/permission.service';

@Component({
  selector: 'globot-leads',
  templateUrl: './leads.component.html',
  styleUrls: ['./leads.component.scss'],
})
export class LeadsComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  botId: string = '';
  leads: Lead[] = [];
  isLoading = false;

  // Pagination
  totalRecords = 0;
  currentPage = 1;
  pageSize = 20;
  first = 0;

  // Filters
  searchTerm = '';
  selectedStatus: string | null = null;
  selectedChannel: string | null = null;
  rangeDates: Date[] | null = null;

  // Status options
  statusOptions = [
    { label: 'Todos', value: null },
    { label: 'Nuevo', value: 'nuevo' },
    { label: 'Contactado', value: 'contactado' },
    { label: 'Convertido', value: 'convertido' },
    { label: 'Perdido', value: 'perdido' },
  ];

  channelOptions = [
    { label: 'Todos', value: null },
    { label: 'Web', value: 'WEB' },
    { label: 'WhatsApp', value: 'WHATSAPP' },
    { label: 'Messenger', value: 'MESSENGER' },
    { label: 'Instagram', value: 'INSTAGRAM' },
    { label: 'Slack', value: 'SLACK' },
    { label: 'Discord', value: 'DISCORD' },
  ];

  // All statuses (open transitions — any status can move to any other)
  allStatuses: LeadStatus[] = ['nuevo', 'contactado', 'convertido', 'perdido'];

  // Pre-computed transitions map (avoids re-creating arrays every CD cycle)
  private transitionsMap = new Map<LeadStatus, { label: string; value: LeadStatus }[]>(
    this.allStatuses.map((status) => [
      status,
      this.allStatuses
        .filter((s) => s !== status)
        .map((s) => ({ label: this.getStatusLabel(s), value: s })),
    ])
  );

  // Toggle
  leadsEnabled = true;

  // Conversation dialog
  visible = false;
  selectedChatHistory: ChatHistoryGroup[] = [];

  // Permissions
  canEdit = false;
  canExport = false;

  constructor(
    private dashboardService: DashboardService,
    private botService: BotService,
    private aRoute: ActivatedRoute,
    private router: Router,
    private ms: MessageService,
    private dateFormatService: DateFormatService,
    private permissionService: PermissionService
  ) {
    const sections = this.router.routerState.snapshot.url.split('/');
    this.botId = sections[3] || this.aRoute.snapshot.paramMap.get('id') || '';
  }

  ngOnInit(): void {
    // Check permissions (reactive to avoid race condition on refresh)
    this.subscription.add(
      this.permissionService
        .hasPermission$('leads.edit')
        .pipe(takeUntil(this.destroy$))
        .subscribe((v) => (this.canEdit = v))
    );
    this.subscription.add(
      this.permissionService
        .hasPermission$('leads.export')
        .pipe(takeUntil(this.destroy$))
        .subscribe((v) => (this.canExport = v))
    );

    // Load bot config
    this.subscription.add(
      this.botService
        .getBots()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (bots) => {
            const bot = bots.find((b: any) => b.id === this.botId);
            if (bot) {
              this.leadsEnabled = bot.leads_enabled || false;
            }
          },
        })
    );

    // Debounced search
    this.subscription.add(
      this.searchSubject
        .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
        .subscribe(() => {
          this.resetPagination();
          this.loadLeads();
        })
    );

    // Default date range: 30 days
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    this.rangeDates = [thirtyDaysAgo, now];

    this.loadLeads();
  }

  loadLeads(event?: any): void {
    this.isLoading = true;

    if (event) {
      this.first = event.first;
      this.currentPage = event.first / event.rows + 1;
    }

    const dateFrom = this.rangeDates?.[0] ? this.formatDate(this.rangeDates[0]) : undefined;
    const dateTo = this.rangeDates?.[1] ? this.formatDate(this.rangeDates[1]) : undefined;

    this.dashboardService
      .getLeads(this.botId, {
        status: this.selectedStatus ?? undefined,
        channel: this.selectedChannel ?? undefined,
        search: this.searchTerm ?? undefined,
        date_from: dateFrom ?? undefined,
        date_to: dateTo ?? undefined,
        page: this.currentPage,
        page_size: this.pageSize,
        sort_by: 'created_at',
        sort_order: 'desc',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: PaginatedLeadsResponse) => {
          this.leads = res.items || [];
          this.totalRecords = res.total || 0;
          this.isLoading = false;
        },
        error: () => {
          this.leads = [];
          this.totalRecords = 0;
          this.isLoading = false;
        },
      });
  }

  onSearchInput(): void {
    this.searchSubject.next(this.searchTerm);
  }

  onFilterChange(): void {
    this.resetPagination();
    this.loadLeads();
  }

  onDateSelect(): void {
    if (this.rangeDates && this.rangeDates.length === 2 && this.rangeDates[1]) {
      this.resetPagination();
      this.loadLeads();
    }
  }

  onStatusChange(lead: Lead, newStatus: LeadStatus): void {
    this.dashboardService
      .updateLeadStatus(this.botId, lead.id, newStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updated) => {
          // Update lead in-place
          const idx = this.leads.findIndex((l) => l.id === lead.id);
          if (idx >= 0) {
            this.leads[idx] = { ...this.leads[idx], ...updated };
          }
          this.showMessage('success', 'Estado actualizado', `Lead actualizado a "${newStatus}"`);
        },
        error: () => {
          this.showMessage('error', 'Error', 'No se pudo actualizar el estado');
        },
      });
  }

  exportToExcel(): void {
    this.isLoading = true;
    const dateFrom = this.rangeDates?.[0] ? this.formatDate(this.rangeDates[0]) : undefined;
    const dateTo = this.rangeDates?.[1] ? this.formatDate(this.rangeDates[1]) : undefined;

    this.dashboardService
      .exportLeads(this.botId, {
        status: this.selectedStatus ?? undefined,
        channel: this.selectedChannel ?? undefined,
        search: this.searchTerm ?? undefined,
        date_from: dateFrom ?? undefined,
        date_to: dateTo ?? undefined,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'leads_data.xlsx';
          link.click();
          window.URL.revokeObjectURL(url);
          this.isLoading = false;
        },
        error: () => {
          this.showMessage('error', 'Error', 'No se pudo exportar los leads');
          this.isLoading = false;
        },
      });
  }

  onLeadsToggle(event: any): void {
    const newConfig = {
      leads_enabled: event.checked,
      user_data_gathering_enabled: event.checked,
    };

    this.dashboardService
      .updateLeadsConfig(this.botId, newConfig)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.leadsEnabled = event.checked;
          if (event.checked) {
            this.showMessage(
              'success',
              'Leads activados',
              'Tu asistente detectará oportunidades de venta'
            );
            this.loadLeads();
          } else {
            this.showMessage(
              'warn',
              'Leads desactivados',
              'Tu asistente no capturará nuevos leads'
            );
          }
        },
        error: () => {
          this.leadsEnabled = !event.checked;
          this.showMessage('error', 'Error', 'No se pudo actualizar la configuración');
        },
      });
  }

  showDialog(correlationId: string): void {
    if (!correlationId) return;

    this.botService
      .getChatHistory(this.botId, correlationId, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rawGroups) => {
          // GLOB-1888: la conversación puede venir vacía (o `null` contra un backend
          // que todavía responda 204); el diálogo se abre con su estado vacío.
          this.selectedChatHistory = (rawGroups ?? []).map((group: any) => ({
            date: this.getGroupDate(group.messages),
            messages: group.messages.map((message: any) => ({ ...message })),
          }));
          this.visible = true;
        },
        error: () => {
          this.showMessage('error', 'Error', 'No se pudo cargar la conversación');
        },
      });
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      nuevo: 'Nuevo',
      contactado: 'Contactado',
      convertido: 'Convertido',
      perdido: 'Perdido',
    };
    return labels[status] || status;
  }

  getStatusSeverity(status: string): string {
    const severities: Record<string, string> = {
      nuevo: 'info',
      contactado: 'warning',
      convertido: 'success',
      perdido: 'danger',
    };
    return severities[status] || 'info';
  }

  getAvailableTransitions(status: string): { label: string; value: LeadStatus }[] {
    return this.transitionsMap.get(status as LeadStatus) ?? [];
  }

  private resetPagination(): void {
    this.currentPage = 1;
    this.first = 0;
  }

  private formatDate(date: Date): string | null {
    if (!date) return null;
    const d = new Date(date);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const day = d.getDate();
    return `${y}-${m < 10 ? '0' : ''}${m}-${day < 10 ? '0' : ''}${day}`;
  }

  private getGroupDate(messages: ChatHistory[]): string {
    if (!messages || messages.length === 0) return 'Sin fecha';
    return this.dateFormatService.formatGroupDate(new Date(messages[0].created_at));
  }

  private showMessage(severity: string, summary: string, detail: string): void {
    this.ms.add({ key: 'tst', severity, summary, detail });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.searchSubject.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
